import { FLIGHTS_CB, FLIGHTS_CB_GLOBAL, FLIGHTS_CB_GOIBIBO } from "./Constants";

// In local dev (NODE_ENV !== "production"), route the upstream flights-cb
// hosts through Next.js rewrites in next.config.js to avoid browser CORS
// errors when running on http://dev.makemytrip.com:3000/.
const IS_DEV = process.env.NODE_ENV !== "production";
const DEV_PROXY_BY_BASE = {
  [FLIGHTS_CB]: "/dev-proxy/flights-cb",
  [FLIGHTS_CB_GLOBAL]: "/dev-proxy/flights-cb-global",
  [FLIGHTS_CB_GOIBIBO]: "/dev-proxy/flights-cb-goibibo",
};

function resolveAutosuggestBase(base) {
  if (!IS_DEV || typeof window === "undefined") return base;
  return DEV_PROXY_BY_BASE[base] || base;
}

/**
 * Browser-only headers for flights-cb autosuggest (matches static SEO widget pattern).
 * @returns {Record<string, string>}
 */
export function getFlightStatusAutosuggestHeaders() {
  if (typeof window === "undefined") {
    return {};
  }
  const w = window;
  const mcid = w?.mcid ?? w?.tokenJson?.tokensList?.mcid ?? w?.tokenJson?.mcid;
  const deviceId = w?.devId ?? w?.deviceId;
  return {
    "app-ver": "1.0.0",
    "device-id": deviceId,
    mcid,
    os: "macOS",
    pfm: "DESKTOP",
    src: "mmt",
    "mmt-auth": "",
    preventGeneric: "true",
  };
}

export function normalizeAutosuggestIata(raw) {
  return String(raw ?? "")
    .replace(/"/g, "")
    .trim()
    .toUpperCase();
}

function clientIsGlobalFlow() {
  if (typeof window === "undefined") return false;
  const ls = window.localeSettings;
  return !!(ls?.isGlobalFlagEnabled && String(ls?.entityName || "").toLowerCase() === "global");
}

function clientIsGoibiboFlow() {
  if (typeof window === "undefined") return false;
  return !!window.localeSettings?.isGoibibo;
}

function pickFlightsCbBase(isGlobalFlow, isGoibiboFlow) {
  if (isGoibiboFlow) return FLIGHTS_CB_GOIBIBO;
  return isGlobalFlow ? FLIGHTS_CB_GLOBAL : FLIGHTS_CB;
}

/**
 * @param {object} opts
 * @param {string} opts.query
 * @param {string} [opts.region]
 * @param {string} [opts.language]
 * @param {string} [opts.currency]
 * @param {number} [opts.limit]
 * @param {boolean} [opts.isGlobalFlow]
 * @param {boolean} [opts.isGoibiboFlow]
 * @returns {Promise<{ title: string; subTitle: string; data: Array<Record<string, unknown>> }>}
 */
export async function fetchFlightStatusAutosuggest({
  query = "",
  region,
  language,
  currency,
  limit = 15,
  isGlobalFlow,
  isGoibiboFlow,
}) {
  const globalFlow = typeof isGlobalFlow === "boolean" ? isGlobalFlow : clientIsGlobalFlow();
  const goibiboFlow =
    typeof isGoibiboFlow === "boolean" ? isGoibiboFlow : clientIsGoibiboFlow();
  const base = resolveAutosuggestBase(pickFlightsCbBase(globalFlow, goibiboFlow));
  const ls = typeof window !== "undefined" ? window.localeSettings : null;
  const tokenJson = typeof window !== "undefined" ? window.tokenJson : null;
  const regionUse = String(region || tokenJson?.region || ls?.region || "in").toLowerCase();
  const languageUse = language || ls?.language || "eng";
  const currencyUse = String(
    currency || tokenJson?.tokensList?.currency_code || ls?.userCurrency || ls?.currency || "INR",
  ).toUpperCase();
  const params = new URLSearchParams({
    limit: String(limit),
    query,
    region: regionUse,
    language: languageUse,
    currency: currencyUse,
  });
  const url = `${base}/api/flights-search/autosuggest?${params.toString()}`;
  const headers = getFlightStatusAutosuggestHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers,
    credentials: "include",
  });
  const json = await response.json();
  const suggestions = json?.results?.SUGGESTIONS;
  const data = suggestions?.data;
  return {
    title: suggestions?.title || "SUGGESTIONS",
    subTitle: suggestions?.subTitle || "",
    data: Array.isArray(data) ? data : [],
  };
}
